//------------------------------------------------------ externos
import { useState, useMemo } from "react";
import { Link, useOutletContext } from "react-router-dom";
import Select from "../components/inputs/SelectBase";
//------------------------------------------------------ elementos
//------------------------------------------------------ funciones
import { useRealtime } from "../services/store";
import { borrarViaje, crearViaje } from "../services/cols/viajes.service";
import { createCliente } from "../services/cols/clientes.service";
import { formatearFecha } from "../functions/dataFunctions";
import { useAuth } from "../auth/AuthContext";
import ChipUsuario from "../components/funcionales/ChipUsuario";
import Patente from "../components/funcionales/Patente";
import { enAR } from "../shared/fechaAr";
import "./css/Sections.css";
import "./css/Viajes.css";

const ahoraDatetimeLocal = () => {
  // El «ahora» del input es el argentino, no el de la máquina (fechaAr.js).
  return enAR(new Date()).toISOString().slice(0, 16);
};

const ESTADO_LABEL = {
  en_curso: { texto: "En curso", clase: "info" },
  finalizado: { texto: "Finalizado", clase: "ok" },
};

const Viajes = () => {
  console.log("[Render] Seccion Viajes");

  const { user } = useAuth();
  const viajes = useRealtime("viajes");
  const choferes = useRealtime("choferes");
  const furgones = useRealtime("furgones");
  const clientes = useRealtime("clientes");

  const [mostrarForm, setMostrarForm] = useState(false);
  const [chofer, setChofer] = useState(null);
  const [furgon, setFurgon] = useState(null);
  const [cliente, setCliente] = useState(null);
  const [detalle, setDetalle] = useState("");
  const [fechaInicio, setFechaInicio] = useState(ahoraDatetimeLocal());
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState("");

  // filtros por columna -- "estado" usa un select de opciones fijas,
  // el resto son texto libre (coincidencia parcial, sin distinguir
  // mayúsculas/minúsculas)
  const [filtros, setFiltros] = useState({
    id: "",
    chofer: "",
    cliente: "",
    furgon: "",
    inicio: "",
    interno: "",
    cargas: "",
    estado: "",
    cargadoPor: "",
  });
  const setFiltro = (campo, valor) =>
    setFiltros((f) => ({ ...f, [campo]: valor }));

  // alta rapida de cliente, inline
  /* BORRAR NO ES ANULAR, y por eso vive detrás de su propio permiso.
   *
   * Anular deja constancia de que alguien decidió no hacer el viaje —es parte
   * de la operación—; borrar hace desaparecer la fila y sólo sirve para
   * limpiar pruebas. Es la misma distinción que la ticketera y movimientos ya
   * sostienen.
   *
   * `admin.borrar` es la MISMA llave que verifica la política del servidor.
   * Con dos definiciones —una acá y un uuid en la base— el día que difieran
   * «no veo el botón» y «no tengo el permiso» se verían igual, y eso ya nos
   * costó dos vueltas en la ticketera.
   *
   * `null` es «los permisos todavía no llegaron» y ahí NO se dibuja: al revés
   * que en el resto del hub, donde ante la duda se muestra. Un botón que
   * destruye datos no puede aparecer por defecto medio segundo. */
  const { permisos = null } = useOutletContext() || {};
  const puedeBorrar = Array.isArray(permisos) && permisos.includes("admin.borrar");
  const [porBorrar, setPorBorrar] = useState(null);   // el viaje que se está confirmando
  const [borrando, setBorrando] = useState(false);
  const [errorBorrar, setErrorBorrar] = useState("");

  /* Sólo un SI escribe, y la confirmación va en la PÁGINA y no en un
   * `confirm()`. Esta pantalla vive adentro del iframe del hub, y ahí Chrome
   * ofrece «impedir que esta página cree más diálogos»: tildado una vez, el
   * botón deja de hacer nada sin decir por qué. Es la lección de la pantalla
   * de geocercas.
   *
   * Y el cartel dice QUÉ se lleva puesto —el número y el cliente— delante del
   * botón rojo: «¿seguro?» a secas se contesta sin mirar. */
  const confirmarBorrado = async () => {
    if (!porBorrar) return;
    setBorrando(true);
    setErrorBorrar("");
    try {
      await borrarViaje(porBorrar.id);
      setPorBorrar(null);
    } catch (e) {
      // No se cierra el cartel: el mensaje tiene que quedar donde estaba el
      // botón que se apretó. Cerrándolo, el error aparece arriba de una tabla
      // que no cambió y se lee como que no pasó nada.
      setErrorBorrar(e.message || "No se pudo borrar");
    } finally {
      setBorrando(false);
    }
  };

  const [nuevoCliente, setNuevoCliente] = useState(false);
  const [nombreNuevoCliente, setNombreNuevoCliente] = useState("");
  const [creandoCliente, setCreandoCliente] = useState(false);

  const opcionesChoferes = choferes.map((p) => ({
    value: p.id,
    label: p.nombreCompleto || p.id,
  }));
  const opcionesFurgones = furgones.map((f) => ({
    value: f.id,
    label: f.label || `${f.id} (${f.dominio})`,
  }));
  const opcionesClientes = clientes.map((c) => ({
    value: c.id,
    label: c.nombre,
  }));

  const limpiarForm = () => {
    setChofer(null);
    setFurgon(null);
    setCliente(null);
    setDetalle("");
    setFechaInicio(ahoraDatetimeLocal());
    setMostrarForm(false);
  };

  const handleCrearCliente = async () => {
    if (!nombreNuevoCliente.trim()) return;
    setCreandoCliente(true);
    try {
      const id = await createCliente({ nombre: nombreNuevoCliente.trim() });
      setCliente({ value: id, label: nombreNuevoCliente.trim() });
      setNombreNuevoCliente("");
      setNuevoCliente(false);
    } catch (e) {
      setError(e.message || "No se pudo crear el cliente.");
    } finally {
      setCreandoCliente(false);
    }
  };

  const handleCrearViaje = async (e) => {
    e.preventDefault();
    if (!chofer || !fechaInicio) {
      setError("Elegí al menos chofer y fecha de inicio.");
      return;
    }
    setError("");
    setGuardando(true);
    try {
      await crearViaje({
        chofer: chofer.value,
        furgon: furgon?.value || "",
        cliente: cliente?.value || "",
        detalle,
        fechaInicio,
        usuario: user?.email || null,
      });
      limpiarForm();
    } catch (e) {
      setError(e.message || "No se pudo crear el viaje.");
    } finally {
      setGuardando(false);
    }
  };

  const contiene = (valor, busqueda) =>
    String(valor || "").toLowerCase().includes(busqueda.toLowerCase());

  const viajesFiltrados = useMemo(() => {
    return viajes.filter((v) => {
      const estadoTexto = ESTADO_LABEL[v.estado]?.texto || "";
      return (
        contiene(v.id, filtros.id) &&
        contiene(v.nombreChofer, filtros.chofer) &&
        contiene(v.nombreCliente, filtros.cliente) &&
        contiene(v.dominioFurgon, filtros.furgon) &&
        contiene(formatearFecha(v.fechaInicio), filtros.inicio) &&
        contiene(v.internoActual, filtros.interno) &&
        contiene(v.cantidadCargas, filtros.cargas) &&
        (filtros.estado === "" || estadoTexto === filtros.estado) &&
        contiene(v.autor?.nombre || v.usuario, filtros.cargadoPor)
      );
    });
  }, [viajes, filtros]);

  return (
    <section className="section">

      <div className="section-container">
        <div className="viajes-toolbar">
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => setMostrarForm((v) => !v)}
          >
            {mostrarForm ? "Cancelar" : "+ Nuevo viaje"}
          </button>
        </div>

        {mostrarForm && (
          <form className="viajes-form" onSubmit={handleCrearViaje}>
            <div className="viajes-form__field">
              <label className="field-label">Chofer *</label>
              <Select
                options={opcionesChoferes}
                value={chofer}
                onChange={setChofer}
                placeholder="Seleccionar chofer..."
                noOptionsMessage={() => "Sin choferes"}
                isClearable
              />
            </div>

            <div className="viajes-form__field">
              <label className="field-label">Furgón</label>
              <Select
                options={opcionesFurgones}
                value={furgon}
                onChange={setFurgon}
                placeholder="Seleccionar furgón..."
                noOptionsMessage={() => "Sin furgones"}
                isClearable
              />
            </div>

            <div className="viajes-form__field">
              <label className="field-label">Cliente</label>
              {!nuevoCliente ? (
                <div className="viajes-form__cliente-row">
                  <Select
                    options={opcionesClientes}
                    value={cliente}
                    onChange={setCliente}
                    placeholder="Seleccionar cliente..."
                    noOptionsMessage={() => "Sin clientes"}
                    isClearable
                    className="viajes-form__cliente-select"
                  />
                  <button
                    type="button"
                    className="btn btn--secondary"
                    onClick={() => setNuevoCliente(true)}
                    title="Crear cliente nuevo"
                  >
                    +
                  </button>
                </div>
              ) : (
                <div className="viajes-form__cliente-row">
                  <input
                    type="text"
                    placeholder="Nombre del cliente nuevo"
                    value={nombreNuevoCliente}
                    onChange={(e) => setNombreNuevoCliente(e.target.value)}
                  />
                  <button
                    type="button"
                    className="btn btn--primary"
                    onClick={handleCrearCliente}
                    disabled={creandoCliente}
                  >
                    {creandoCliente ? "..." : "Crear"}
                  </button>
                  <button
                    type="button"
                    className="btn btn--ghost"
                    onClick={() => setNuevoCliente(false)}
                  >
                    Cancelar
                  </button>
                </div>
              )}
            </div>

            <div className="viajes-form__field">
              <label className="field-label">Detalle</label>
              <input
                type="text"
                value={detalle}
                onChange={(e) => setDetalle(e.target.value)}
                placeholder="Ej: entrega en depósito Bahía Blanca"
              />
            </div>

            <div className="viajes-form__field">
              <label className="field-label">Fecha y hora de inicio *</label>
              <input
                type="datetime-local"
                value={fechaInicio}
                onChange={(e) => setFechaInicio(e.target.value)}
              />
            </div>

            <button type="submit" className="btn btn--primary" disabled={guardando}>
              {guardando ? "Creando..." : "Crear viaje"}
            </button>
          </form>
        )}

        {error && <p className="combustible-error">{error}</p>}

        {/* LO DESTRUCTIVO SE PREGUNTA APARTE, con la lista de lo que se lleva
            puesto DELANTE del botón. Pegado abajo de la fila se lee como parte
            de la tabla y se contesta sin mirarlo — es la regla que el bot ya
            aplica al borrado de tarjetas de taller por WhatsApp. */}
        {porBorrar && (
          <div className="viajes-borrar" role="alertdialog" aria-live="assertive">
            <p className="viajes-borrar__que">
              Vas a borrar el viaje <strong>{porBorrar.id}</strong>
              {porBorrar.nombreChofer ? ` de ${porBorrar.nombreChofer}` : ""}
              {porBorrar.nombreCliente ? ` para ${porBorrar.nombreCliente}` : ""}
              {porBorrar.cantidadCargas
                ? `, con ${porBorrar.cantidadCargas} carga${
                    porBorrar.cantidadCargas === 1 ? "" : "s"
                  } asociada${porBorrar.cantidadCargas === 1 ? "" : "s"}`
                : ""}
              .
            </p>
            {/* No es «anular»: no queda constancia de que existió. Decirlo acá
                y no en el título, que se saltea. */}
            <p className="viajes-borrar__aviso">
              Esto <strong>no es anular</strong>: la fila desaparece y no queda
              constancia de que el viaje existió. No se puede deshacer.
            </p>
            {errorBorrar && (
              <p className="viajes-borrar__error">{errorBorrar}</p>
            )}
            <div className="viajes-borrar__botones">
              <button
                type="button"
                className="btn-borrar btn-borrar--confirmar"
                onClick={confirmarBorrado}
                disabled={borrando}
              >
                {borrando ? "Borrando…" : `Sí, borrar ${porBorrar.id}`}
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => {
                  setPorBorrar(null);
                  setErrorBorrar("");
                }}
                disabled={borrando}
              >
                No, dejarlo
              </button>
            </div>
          </div>
        )}

        <div className="table-scroll-wrapper">
          <table className="table-lista table-lista--tarjetas">
            <thead className="table-titles">
              <tr>
                <th>Viaje</th>
                <th>Chofer</th>
                <th>Cliente</th>
                <th>Furgón</th>
                <th>Inicio</th>
                <th>Interno actual</th>
                <th>Cargas</th>
                <th>Estado</th>
                <th>Cargado por</th>
                {/* La columna sólo existe para quien puede borrar: una vacía
                    para todos los demás es una columna que no dice nada. */}
                {puedeBorrar && <th aria-label="Borrar"></th>}
              </tr>
              <tr className="table-filtros">
                <th>
                  <input
                    type="text"
                    placeholder="Filtrar..."
                    value={filtros.id}
                    onChange={(e) => setFiltro("id", e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Filtrar..."
                    value={filtros.chofer}
                    onChange={(e) => setFiltro("chofer", e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Filtrar..."
                    value={filtros.cliente}
                    onChange={(e) => setFiltro("cliente", e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Filtrar..."
                    value={filtros.furgon}
                    onChange={(e) => setFiltro("furgon", e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Filtrar..."
                    value={filtros.inicio}
                    onChange={(e) => setFiltro("inicio", e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Filtrar..."
                    value={filtros.interno}
                    onChange={(e) => setFiltro("interno", e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Filtrar..."
                    value={filtros.cargas}
                    onChange={(e) => setFiltro("cargas", e.target.value)}
                  />
                </th>
                <th>
                  <select
                    value={filtros.estado}
                    onChange={(e) => setFiltro("estado", e.target.value)}
                  >
                    <option value="">Todos</option>
                    <option value="En curso">En curso</option>
                    <option value="Finalizado">Finalizado</option>
                  </select>
                </th>
                <th>
                  <input
                    type="text"
                    placeholder="Filtrar..."
                    value={filtros.cargadoPor}
                    onChange={(e) => setFiltro("cargadoPor", e.target.value)}
                  />
                </th>
                {puedeBorrar && <th></th>}
              </tr>
            </thead>
            <tbody>
              {viajesFiltrados.length === 0 && (
                <tr>
                  <td colSpan={puedeBorrar ? 10 : 9}>
                    {viajes.length === 0
                      ? "No hay viajes registrados todavía."
                      : "Ningún viaje coincide con los filtros."}
                  </td>
                </tr>
              )}
              {viajesFiltrados.map((v) => {
                const estado = ESTADO_LABEL[v.estado] || ESTADO_LABEL.en_curso;
                return (
                  <tr key={v.id}>
                    <td className="celda--id">
                      <Link to={`/viajes/${v.id}`} className="plate-chip plate-chip--accent">
                        {v.id}
                      </Link>
                    </td>
                    <td className="celda--sec" data-vacio={v.nombreChofer ? undefined : ""}>
                      {v.nombreChofer || "—"}
                    </td>
                    <td className="celda--sec" data-vacio={v.nombreCliente ? undefined : ""}>
                      {v.nombreCliente || "—"}
                    </td>
                    {/* La patente del furgón como chapa Mercosur (Igna, 03/10:
                        «quedan re facheras»). El número del viaje de al lado
                        sigue en `plate-chip`: no es una patente. */}
                    <td className="celda--sec" data-vacio={v.dominioFurgon ? undefined : ""}>
                      {v.dominioFurgon ? <Patente dominio={v.dominioFurgon} /> : "—"}
                    </td>
                    <td className="celda--sec">{formatearFecha(v.fechaInicio)}</td>
                    <td className="celda--sec" data-vacio={v.internoActual ? undefined : ""}>
                      {v.internoActual || "—"}
                    </td>
                    <td className="celda--sec">
                      {v.cantidadCargas}
                      {/* En la tabla la columna ya se llama "Cargas"; en la tarjeta no
                          hay encabezado y un número suelto entre puntos no dice nada. */}
                      <span className="solo-en-tarjeta">
                        {" "}carga{v.cantidadCargas === 1 ? "" : "s"}
                      </span>
                    </td>
                    <td className="celda--estado">
                      <span className={`pill ${estado.clase}`}>{estado.texto}</span>
                    </td>
                    <td className="celda--solo-tabla">
                      <ChipUsuario fila={v} autor={v.autor} />
                    </td>
                    {puedeBorrar && (
                      <td className="celda--borrar">
                        <button
                          type="button"
                          className="btn-borrar"
                          title={`Borrar el viaje ${v.id}`}
                          onClick={() => {
                            setErrorBorrar("");
                            setPorBorrar(v);
                          }}
                        >
                          Borrar
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
};

export default Viajes;
